/**
 * Pure helpers for the guided duplicate merge (TRUS-558 merge v2).
 *
 * The server decides what a merge does (member_dedup_service.project_merged_profile
 * and plan_cases); these helpers only turn the reviewer's on-screen choices into the
 * request body and decide when the merge button may be pressed. Kept free of React
 * so they are unit tested on their own.
 */

import type {
  CaseDecision,
  MergeCase,
  MergePreviewField,
  RevertReport,
} from '@/services/api/members';

/** Mirror of the server's PLACEHOLDER_VALUES; the preview response carries the
 *  authoritative list, this is only the fallback when it is absent. */
export const DEFAULT_PLACEHOLDER_VALUES = ['n/a', 'na', 'none', 'unknown', '-', '--', '?', 'null'];

export function isBlankMergeValue(
  value: unknown,
  placeholders: readonly string[] = DEFAULT_PLACEHOLDER_VALUES
): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value !== 'string') return false;
  const v = value.trim().toLowerCase();
  return v === '' || placeholders.includes(v);
}

/** Every record's value for a field, survivor first. */
export function fieldOptions(
  field: MergePreviewField,
  survivorId: number,
  placeholders?: readonly string[]
): { memberId: number; value: unknown; blank: boolean }[] {
  return [
    {
      memberId: survivorId,
      value: field.survivor_value,
      blank: field.survivor_blank ?? isBlankMergeValue(field.survivor_value, placeholders),
    },
    ...field.duplicate_values.map((d) => ({
      memberId: d.member_id,
      value: d.value,
      blank: d.blank ?? isBlankMergeValue(d.value, placeholders),
    })),
  ];
}

/** The member whose value a field takes when the reviewer makes no choice: the
 *  survivor when it has a value, else the duplicate the server fills it from. */
export function defaultWinner(field: MergePreviewField, survivorId: number): number {
  return field.source === 'duplicate' && field.source_member_id != null
    ? field.source_member_id
    : survivorId;
}

/** The value the merged profile shows for a field under the current selection. */
export function mergedValueFor(
  field: MergePreviewField,
  survivorId: number,
  selected: number | undefined
): unknown {
  if (selected === undefined || !field.choosable) return field.merged_value;
  const option = fieldOptions(field, survivorId).find((o) => o.memberId === selected);
  if (!option || (option.blank && selected !== survivorId)) return field.merged_value;
  return option.value;
}

/** field_choices for the merge request: only fields where the reviewer picked a
 *  different record than the default, and only choosable fields. */
export function buildFieldChoices(
  fields: MergePreviewField[],
  survivorId: number,
  selection: Record<string, number>
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of fields) {
    const picked = selection[f.field];
    if (!f.choosable || picked === undefined) continue;
    if (picked !== defaultWinner(f, survivorId)) out[f.field] = picked;
  }
  return out;
}

export interface CaseDecisionState {
  /** Cases that move with the person without a decision. */
  auto: MergeCase[];
  /** Cases in a program the survivor already has a case in. */
  required: MergeCase[];
  /** Enrollment ids of required cases with no decision yet. */
  undecided: number[];
}

export function caseDecisionState(
  cases: MergeCase[] | undefined,
  decisions: Record<string, CaseDecision>
): CaseDecisionState {
  const all = cases ?? [];
  const required = all.filter((c) => c.decision_required);
  return {
    auto: all.filter((c) => !c.decision_required),
    required,
    undecided: required
      .filter((c) => !decisions[String(c.enrollment_id)])
      .map((c) => c.enrollment_id),
  };
}

/** case_decisions for the merge request: one entry per case that needs one. */
export function buildCaseDecisions(
  cases: MergeCase[] | undefined,
  decisions: Record<string, CaseDecision>
): Record<string, CaseDecision> {
  const out: Record<string, CaseDecision> = {};
  for (const c of cases ?? []) {
    const d = decisions[String(c.enrollment_id)];
    if (c.decision_required && d) out[String(c.enrollment_id)] = d;
  }
  return out;
}

export function canSubmitMerge(
  cases: MergeCase[] | undefined,
  decisions: Record<string, CaseDecision>
): boolean {
  return caseDecisionState(cases, decisions).undecided.length === 0;
}

/**
 * What the wizard's final button does. A person can have only one case per
 * program, so "Don't merge" on ANY same-program case means the profiles are not
 * merged at all: the group is dismissed and both profiles are kept.
 */
export function mergeOutcome(
  cases: MergeCase[] | undefined,
  decisions: Record<string, CaseDecision>
): 'merge' | 'keep_both' {
  return (cases ?? []).some(
    (c) => c.decision_required && decisions[String(c.enrollment_id)] === 'dont_merge'
  )
    ? 'keep_both'
    : 'merge';
}

/** Total rows a journal summary says the merge changed. */
export function countChangedRows(
  changed: Record<string, Record<string, number>> | undefined
): number {
  if (!changed) return 0;
  return Object.values(changed).reduce(
    (n, byTable) => n + Object.values(byTable).reduce((a, b) => a + b, 0),
    0
  );
}

export interface RevertConflictRow {
  table: string;
  pk: number | string;
  column: string;
  field?: string;
  reason: string;
  mergeWrote: unknown;
  current: unknown;
}

/** One row per (record, column) a revert left alone because it changed since. */
export function revertConflictRows(report: RevertReport | null | undefined): RevertConflictRow[] {
  if (!report) return [];
  const rows: RevertConflictRow[] = [];
  for (const c of report.conflicts ?? []) {
    for (const [column, v] of Object.entries(c.columns ?? {})) {
      rows.push({
        table: c.table,
        pk: c.pk,
        column,
        field: c.field,
        reason: c.reason,
        mergeWrote: v.merge_wrote,
        current: v.current,
      });
    }
  }
  return rows;
}
